import { apiUrl, currentUserId, currentUserQuery } from "./auth";

export type PlanningPerson = {
  id: number;
  name: string;
  role: string;
  store: string;
  weekly_hours: number;
  photo: boolean;
};

export type PlanningStatus = "saisir" | "cours" | "validee" | "absent";

export type PlanningDayKind = "vide" | "presence" | "cp" | "recup" | "maladie" | "repos" | "absence";

export type PlanningDay = {
  label: string;
  hours: number;
  planned: number;
  kind: PlanningDayKind;
};

export type PlanningRow = PlanningPerson & {
  hours: number;
  status: PlanningStatus;
  motif: string;
  cp: number;
  overtime: number;
  deficit: number;
  cumul: number;
  days: PlanningDay[];
};

export type PlanningView = {
  ready: boolean;
  message: string;
  week: string;
  people: PlanningPerson[];
  rows: PlanningRow[];
};

function authHeaders(extra?: Record<string, string>): HeadersInit {
  const id = currentUserId();
  return { ...(id ? { "X-User-Id": id } : {}), ...extra };
}

async function read(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function asPerson(row: PlanningPerson): PlanningPerson | null {
  const id = Number(row?.id) || 0;
  if (id <= 0) return null;
  return {
    id,
    name: String(row.name || "Salarié"),
    role: String(row.role || ""),
    store: String(row.store || ""),
    weekly_hours: Number(row.weekly_hours) || 0,
    photo: Boolean((row as PlanningPerson).photo),
  };
}

export async function loadPlanning(week: string, ids: number[]): Promise<PlanningView> {
  const query = new URLSearchParams();
  if (week) query.set("week", week);
  if (ids.length > 0) query.set("ids", ids.join(","));
  const glue = currentUserQuery().includes("?") ? "&" : "?";
  const res = await fetch(`${apiUrl("planning.php")}${currentUserQuery()}${glue}${query.toString()}`, {
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Planning indisponible"));
  }
  const people = Array.isArray(data.people) ? data.people.flatMap((row) => {
    const person = asPerson(row as PlanningPerson);
    return person ? [person] : [];
  }) : [];
  const rows = Array.isArray(data.rows) ? data.rows.flatMap((row) => {
    const person = asPerson(row as PlanningPerson);
    if (!person) return [];
    const raw = row as PlanningRow;
    const status = raw.status;
    const kinds = new Set(["vide", "presence", "cp", "recup", "maladie", "repos", "absence"]);
    const days = Array.isArray(raw.days) ? raw.days.flatMap((day) => {
      const kind = String(day?.kind || "");
      if (!kinds.has(kind)) return [];
      return [{
        label: String(day.label || ""),
        hours: Number(day.hours) || 0,
        planned: Number(day.planned) || 0,
        kind: kind as PlanningDay["kind"],
      }];
    }) : [];
    return [{
      ...person,
      hours: Number(raw.hours) || 0,
      status: status === "cours" || status === "validee" || status === "absent" ? status : "saisir",
      motif: String(raw.motif || ""),
      cp: Number(raw.cp) || 0,
      overtime: Number(raw.overtime) || 0,
      deficit: Number(raw.deficit) || 0,
      cumul: Number(raw.cumul) || 0,
      days,
    }];
  }) : [];
  return {
    ready: data.ready !== false,
    message: String(data.message || ""),
    week: String(data.week || week),
    people,
    rows,
  };
}

const photoCache = new Map<number, string>();

export function loadPlanningPhoto(id: number): Promise<string> {
  const known = photoCache.get(id);
  if (known) return Promise.resolve(known);
  const glue = currentUserQuery().includes("?") ? "&" : "?";
  return fetch(`${apiUrl("planning.php")}${currentUserQuery()}${glue}photo=${id}`, {
    credentials: "include",
    cache: "force-cache",
    headers: authHeaders(),
  })
    .then((res) => (res.ok ? res.blob() : null))
    .then((blob) => {
      if (!blob) return "";
      const url = URL.createObjectURL(blob);
      photoCache.set(id, url);
      return url;
    })
    .catch(() => "");
}

export async function savePlanningTeam(ficheId: number, team: number[]): Promise<number[]> {
  const res = await fetch(apiUrl("planning.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ id: ficheId, team }),
  });
  const data = await read(res);
  if (!res.ok || data.success === false) {
    throw new Error(String(data.message || "Équipe non enregistrée"));
  }
  return Array.isArray(data.planning_team) ? data.planning_team.map((id) => Number(id) || 0).filter((id) => id > 0) : [];
}
